/**
 * Presentation hierarchy for the capability catalog: groups Features
 * (capabilities + journeys) under their owning Module, so the admin matrix can
 * render Modules → Features rows. Platform Services are their own group.
 *
 * The dependency DAG is many-to-many; this is a curated PRIMARY-owner mapping
 * for display only. It does not affect resolution.
 */
import { CATALOG, type CapabilityNodeKey } from '@/lib/capabilities/catalog';
import type { AnyCatalogNode, ModuleDefinition } from '@/lib/capabilities/contracts';

/** Primary module each capability is displayed under in the matrix. */
export const CAPABILITY_MODULE: Partial<Record<CapabilityNodeKey, CapabilityNodeKey>> = {
  'capabilities.photo_capture': 'modules.inspections',
  'capabilities.video_capture': 'modules.inspections',
  'capabilities.drawing': 'modules.inspections',
  'capabilities.voice_dictation': 'modules.inspections',
  'capabilities.speech_recognition': 'modules.inspections',
  'capabilities.ml_kit': 'modules.inspections',
  'capabilities.evidence_intelligence': 'modules.inspections',
  'capabilities.wrx_vision': 'modules.inspections',
  'capabilities.wrx_vision_cloud': 'modules.inspections',
  'capabilities.wrx_vision_pack_fleet': 'modules.inspections',
  'capabilities.wrx_vision_pack_equipment': 'modules.inspections',
  'capabilities.wrx_vision_pack_property': 'modules.inspections',
  'capabilities.wrx_vision_pack_safety': 'modules.inspections',
  'capabilities.wrx_vision_training_share': 'modules.inspections',
  'capabilities.wrx_verified': 'modules.jobs',
  'capabilities.qr_codes': 'modules.assets',
  'capabilities.barcode_scan': 'modules.assets',
  'capabilities.work_intelligence': 'modules.jobs',
  'capabilities.work_planning': 'modules.jobs',
  'capabilities.journey_intelligence': 'modules.analytics',
  'capabilities.journey_analytics': 'modules.analytics',
  'capabilities.power_bi_export': 'modules.analytics',
  'capabilities.ai_summary': 'modules.ai',
  'capabilities.ai_assistants': 'modules.ai',
  'capabilities.scheduling': 'modules.automation',
  'capabilities.recurring_work': 'modules.automation',
  'capabilities.approvals': 'modules.requests',
  'capabilities.quotes': 'modules.contractors',
  'capabilities.offline_mode': 'modules.settings',
  'capabilities.notifications': 'modules.settings',
  'capabilities.csv_import': 'modules.integrations',
  'capabilities.excel_import': 'modules.integrations',
  'capabilities.power_automate': 'modules.integrations',
  'capabilities.iot_connectors': 'modules.integrations',
  'capabilities.embed': 'modules.integrations',
  'capabilities.embed_interactive': 'modules.integrations',
  'capabilities.rest_api': 'modules.developer',
  'capabilities.graphql': 'modules.developer',
  'capabilities.webhooks': 'modules.developer',
  'capabilities.mcp': 'modules.developer',
  'capabilities.maintenance_programmes': 'modules.automation',
  'capabilities.schedule_exceptions': 'modules.jobs',
  'capabilities.condition_intelligence': 'modules.assets',
  'capabilities.location_tracking': 'modules.assets',
  'capabilities.knowledge_base': 'modules.assets',
  'capabilities.compliance_register': 'modules.reports',
  'capabilities.asset_360': 'modules.reports',
  'capabilities.esg': 'modules.assets',
  'capabilities.esg_ai_writeup': 'modules.assets',
  'capabilities.site_360': 'modules.reports',
  'capabilities.industry_packs': 'modules.settings',
  'capabilities.field_ai_assist': 'modules.ai',
  'capabilities.group_rules': 'modules.inspections',
  'capabilities.rounds': 'modules.jobs',
  'capabilities.sub_jobs': 'modules.jobs',
  'capabilities.contractor_inspections': 'modules.contractors',
  'capabilities.custom_asset_types': 'modules.assets',
  'capabilities.bulk_asset_setup': 'modules.assets',
  'capabilities.contact_import': 'modules.contacts',
  'capabilities.ai_checklist_generation': 'modules.ai',
  'capabilities.assistant_changes': 'modules.ai',
  'capabilities.assistant_planning': 'modules.ai',
  'capabilities.assistant_insights': 'modules.ai',
  'capabilities.assistant_photo': 'modules.ai',
  'capabilities.assistant_voice': 'modules.ai',
  'capabilities.feature_requests': 'modules.administration',
};

export interface CatalogGroup {
  /** The module node, or null for the Platform Services group. */
  module: ModuleDefinition | null;
  key: string;
  title: string;
  features: AnyCatalogNode[];
}

/** Resolves the display module for any node (self for modules, mapped for capabilities, journey.moduleKey for journeys). */
function ownerModuleKey(node: AnyCatalogNode): CapabilityNodeKey | null {
  if (node.layer === 'module') return node.key as CapabilityNodeKey;
  if (node.layer === 'journey') return node.moduleKey as CapabilityNodeKey;
  if (node.layer === 'capability') return CAPABILITY_MODULE[node.key as CapabilityNodeKey] ?? null;
  return null; // platform_service
}

/**
 * Builds the Modules → Features grouping. Modules are ordered by their catalog
 * order; each carries its journeys + capabilities. Services form a trailing
 * "Platform Services" group; anything unmapped falls into "Other".
 */
export function buildCatalogHierarchy(): CatalogGroup[] {
  const nodes = Object.values(CATALOG) as AnyCatalogNode[];
  const modules = nodes.filter((n): n is ModuleDefinition => n.layer === 'module');

  const groups: CatalogGroup[] = modules.map((m) => ({
    module: m,
    key: m.key,
    title: m.title,
    features: [],
  }));
  const byKey = new Map(groups.map((g) => [g.key, g]));
  const other: CatalogGroup = { module: null, key: '__other', title: 'Other', features: [] };
  const services: CatalogGroup = { module: null, key: '__services', title: 'Platform Services', features: [] };

  for (const node of nodes) {
    if (node.layer === 'module') continue;
    if (node.layer === 'platform_service') {
      services.features.push(node);
      continue;
    }
    const owner = ownerModuleKey(node);
    const group = owner ? byKey.get(owner) : undefined;
    (group ?? other).features.push(node);
  }

  const result = groups.filter((g) => g.features.length > 0 || g.module);
  if (other.features.length > 0) result.push(other);
  result.push(services);
  return result;
}
